import type { CompanyRecord, CompanyUpsertPayload } from "@/lib/admin/companiesStore";
import { isAxiosError } from "axios";

export type CompanyFormState = {
  name: string;
  domain: string;
  email: string;
  phone: string;
  /** Shown on edit only; persisted via owner `users.name` when there is a single company-owner (no company table column). */
  contactPerson: string;
  address: string;
  country: string;
  region: string;
  city: string;
  status: "active" | "inactive";
  logo: string;
  primaryColor: string;
  timezone: string;
  currencyId: number | null;
};

export function emptyCompanyForm(): CompanyFormState {
  return {
    name: "",
    domain: "",
    email: "",
    phone: "",
    contactPerson: "",
    address: "",
    country: "",
    region: "",
    city: "",
    status: "active",
    logo: "",
    primaryColor: "#f59e0b",
    timezone: "",
    currencyId: null,
  };
}

export function companyFormToPayload(f: CompanyFormState): Record<string, unknown> {
  return {
    name: f.name,
    domain: f.domain || null,
    email: f.email || null,
    phone: f.phone || null,
    contactPerson: f.contactPerson?.trim() || null,
    address: f.address || null,
    country: f.country || null,
    region: f.region || null,
    city: f.city || null,
    status: f.status,
    logo: f.logo || null,
    primaryColor: f.primaryColor || null,
    timezone: f.timezone?.trim() || null,
    currency_id: f.currencyId != null ? f.currencyId : null,
  };
}

export function recordToCompanyForm(c: CompanyRecord): CompanyFormState {
  const fromAssigned =
    c.companyAssignedUsers.find((u) => u.role === "company-owner")?.name?.trim() ||
    c.companyAssignedUsers[0]?.name?.trim() ||
    "";
  return {
    name: c.name,
    domain: c.domain ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    contactPerson: fromAssigned,
    address: c.address ?? "",
    country: c.country ?? "",
    region: c.region ?? "",
    city: c.city ?? "",
    status: c.status,
    logo: c.logo ?? "",
    primaryColor: c.primaryColor ?? "#f59e0b",
    timezone: c.timezone ?? "",
    currencyId: c.currencyId ?? null,
  };
}

export function companyFormErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error) return error.message;
  return "Something went wrong. Please try again.";
}
